import { useEffect, useState } from 'react';
import {
  updateOfferteCustomer,
  type OfferteCustomer,
  type UpdateLeadPatch,
} from '../../lib/offertes';

interface Props {
  isOpen: boolean;
  leadId: string;
  customer: OfferteCustomer;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  name: string;
  email: string;
  phone: string;
  street: string;
  postalCode: string;
  city: string;
}

function buildPatch(form: FormState, original: OfferteCustomer): UpdateLeadPatch {
  const patch: UpdateLeadPatch = {};
  const originalName = original.name || `${original.firstName} ${original.lastName}`.trim();
  if (form.name.trim() !== originalName.trim()) patch.Name = form.name.trim();
  if (form.email.trim() !== (original.email || '').trim()) patch.Email = form.email.trim();
  if (form.phone.trim() !== (original.phone || '').trim()) patch.Phone = form.phone.trim();
  if (form.street.trim() !== (original.street || '').trim()) patch.Street = form.street.trim();
  if (form.postalCode.trim() !== (original.postalCode || '').trim()) {
    patch.PostalCode = form.postalCode.trim();
  }
  if (form.city.trim() !== (original.city || '').trim()) patch.City = form.city.trim();
  return patch;
}

export function EditCustomerDialog({ isOpen, leadId, customer, onClose, onSaved }: Props) {
  const [form, setForm] = useState<FormState>({
    name: customer.name || `${customer.firstName} ${customer.lastName}`.trim(),
    email: customer.email,
    phone: customer.phone,
    street: customer.street,
    postalCode: customer.postalCode,
    city: customer.city,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [postcodegebiedFeedback, setPostcodegebiedFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: customer.name || `${customer.firstName} ${customer.lastName}`.trim(),
        email: customer.email,
        phone: customer.phone,
        street: customer.street,
        postalCode: customer.postalCode,
        city: customer.city,
      });
      setError(null);
      setPostcodegebiedFeedback(null);
    }
  }, [isOpen, customer]);

  if (!isOpen) return null;

  const patch = buildPatch(form, customer);
  const hasChanges = Object.keys(patch).length > 0;
  const postcodeChanged = 'PostalCode' in patch;

  async function handleSave() {
    if (!hasChanges) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    setPostcodegebiedFeedback(null);
    try {
      const res = await updateOfferteCustomer(leadId, patch);
      if (postcodeChanged) {
        if (res.postcodegebiedNaam) {
          setPostcodegebiedFeedback(`Postcodegebied opnieuw bepaald: ${res.postcodegebiedNaam}`);
        } else {
          setPostcodegebiedFeedback(
            'Let op: voor deze postcode is geen postcodegebied gevonden. Routes-planning werkt niet automatisch.'
          );
        }
        // Korte zichtbaarheid van de feedback voor de gebruiker, dan sluiten.
        setTimeout(() => {
          onSaved();
          onClose();
        }, 1500);
      } else {
        onSaved();
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-[60]"
        onClick={busy ? undefined : onClose}
      />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md pointer-events-auto">
          <div className="px-5 py-4 border-b border-slate-200">
            <h3 className="text-base font-semibold text-slate-900">Klantgegevens bewerken</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Wijzig contact en adres. Bij een nieuwe postcode wordt het postcodegebied automatisch
              opnieuw bepaald.
            </p>
          </div>

          <div className="px-5 py-4 space-y-3">
            <Field
              label="Naam"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              disabled={busy}
            />
            <Field
              label="E-mail"
              type="email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              disabled={busy}
            />
            <Field
              label="Telefoon"
              type="tel"
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
              disabled={busy}
            />
            <Field
              label="Straat + huisnummer"
              value={form.street}
              onChange={(v) => setForm({ ...form, street: v })}
              disabled={busy}
            />
            <div className="grid grid-cols-3 gap-2">
              <Field
                label="Postcode"
                value={form.postalCode}
                onChange={(v) => setForm({ ...form, postalCode: v })}
                disabled={busy}
                className="col-span-1"
              />
              <Field
                label="Plaats"
                value={form.city}
                onChange={(v) => setForm({ ...form, city: v })}
                disabled={busy}
                className="col-span-2"
              />
            </div>

            {postcodeChanged && !postcodegebiedFeedback && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Postcode wijzigt — postcodegebied wordt opnieuw bepaald bij opslaan.
              </p>
            )}
            {postcodegebiedFeedback && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                {postcodegebiedFeedback}
              </p>
            )}
            {error && (
              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </p>
            )}
          </div>

          <div className="px-5 py-3 border-t border-slate-200 flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-3 py-1.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy || !hasChanges}
              className="px-3 py-1.5 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {busy ? 'Bezig...' : 'Opslaan'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  disabled?: boolean;
  className?: string;
}

function Field({ label, value, onChange, type = 'text', disabled, className }: FieldProps) {
  return (
    <label className={`block text-xs font-medium text-slate-600 ${className ?? ''}`}>
      {label}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="mt-1 w-full px-3 py-2 text-sm text-slate-900 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
      />
    </label>
  );
}
